import React, {FunctionComponent} from 'react';
import {ISessionXpedition} from '../../redux/sessions/interfaces';

import './styles.scss';
import {useNavigate} from 'react-router-dom';
import {
  removeSessionDialogue,
  sessionsAsyncActions
} from '../../redux/pageAsyncActions';
import {useDispatch} from 'react-redux';
import ProgressBar from '../progress-bar';
import {FormattedMessage, useIntl} from 'react-intl';

interface ISessionCardProps {
  session: ISessionXpedition;
  isCurrent: boolean;
}

interface ILockIconProps {
  className?: string;
}

interface IStatusConfig {
  label: string;
  helper: string;
  tone: string;
}

const LockIcon: FunctionComponent<ILockIconProps> = ({className = ''}) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <path d="M7 11V8a5 5 0 0 1 10 0v3" />
  </svg>
);

const getStatusConfig = (
  isLocked: boolean,
  isCompleted: boolean,
  isInProgress: boolean
): IStatusConfig => {
  const intl = useIntl();

  if (isLocked) {
    return {
      label: intl.formatMessage({
        id: 'gritx.sessions.locked',
        defaultMessage: 'Locked'
      }),
      helper: intl.formatMessage({
        id: 'gritx.sessions.toUnlock',
        defaultMessage: 'Complete previous session to unlock'
      }),
      tone: 'status-badge--locked'
    };
  }

  if (isCompleted) {
    return {
      label: intl.formatMessage({
        id: 'gritx.sessions.completed',
        defaultMessage: 'Completed'
      }),
      helper: intl.formatMessage({
        id: 'gritx.sessions.toReview',
        defaultMessage: 'Ready for review anytime'
      }),
      tone: 'status-badge--completed'
    };
  }

  if (isInProgress) {
    return {
      label: intl.formatMessage({
        id: 'gritx.sessions.inProgress',
        defaultMessage: 'In Progress'
      }),
      helper: intl.formatMessage({
        id: 'gritx.sessions.currentAvailableSession',
        defaultMessage: 'This is your current available session'
      }),
      tone: 'status-badge--progress'
    };
  }

  return {
    label: intl.formatMessage({
      id: 'gritx.sessions.notStarted',
      defaultMessage: 'Not Started'
    }),
    helper: intl.formatMessage({
      id: 'gritx.sessions.readyToBegin',
      defaultMessage: 'Ready to begin'
    }),
    tone: 'status-badge--idle'
  };
};

const getProgressBarClass = (
  isLocked: boolean,
  isCompleted: boolean
): string => {
  if (isLocked) {
    return 'progress__bar--locked';
  }

  if (isCompleted) {
    return 'progress__bar--completed';
  }

  return 'progress__bar--primary';
};

const getCardClassName = (
  isLocked: boolean,
  isCurrent: boolean,
  isCompleted: boolean
): string => [
  'session-card',
  isLocked ? 'session-card--locked' : '',
  isCurrent ? 'session-card--current' : '',
  isCompleted ? 'session-card--completed' : ''
]
  .filter(Boolean)
  .join(' ');

const getButtonClassName = (
  baseClass: string,
  disabled: boolean
): string => ['button', baseClass, disabled ? 'button--disabled' : '']
  .filter(Boolean)
  .join(' ');

export const SessionCard: FunctionComponent<ISessionCardProps> = ({
  session,
  isCurrent
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLocked = !(session.hasQuestionnaire || session.order === 1);
  const isCompleted = session.percentage === 100;
  const isInProgress = !!session.dialogId && !isCompleted;
  const isNotStarted = !session.dialogId;

  const statusConfig = getStatusConfig(isLocked, isCompleted, isInProgress);
  const progressBarClass = getProgressBarClass(isLocked, isCompleted);
  const cardClassName = getCardClassName(isLocked, isCurrent, isCompleted);

  const isStartDisabled = isLocked || !isNotStarted;
  const isContinueDisabled = isLocked || !isInProgress;
  const isStartOverDisabled = isLocked || (!isCompleted && !isInProgress);

  function handleStartSession() {
    navigate(`/session/${session.xpeditionId}`);
  }

  async function handleRemoveSession() {
    dispatch(sessionsAsyncActions[removeSessionDialogue](session.dialogId));
  }

  return (
    <article className={cardClassName}>
      <div className="session-card__grid">
        <div className="session-card__media">
          <img
            className="session-card__image"
            src={session.imageUrl}
            alt={session.theme}
          />
          <div className="session-card__overlay" />

          <div className="session-card__session-pill">
            <span className="session-card__session-number">{session.order}</span>
            <span>
              <FormattedMessage
                id={'gritx.sessions.session'}
                defaultMessage="Session"
              /> {session.order}</span>
          </div>

          {isCurrent && !isLocked && (
            <div className="session-card__current-pill">
              <FormattedMessage
                id={'gritx.sessions.currentSession'}
                defaultMessage="Current session"
              />
            </div>
          )}

          {isLocked && (
            <div className="session-card__locked-pill">
              <LockIcon className="session-card__lock-icon" />
              <span>
                <FormattedMessage
                  id={'gritx.sessions.locked'}
                  defaultMessage="Locked"
                />
              </span>
            </div>
          )}
        </div>

        <div className="session-card__content">
          <div className="session-card__head">
            <div className="session-card__info">
              <div className="session-card__status-row">
                <span className={`status-badge ${statusConfig.tone}`}>
                  {statusConfig.label}
                </span>
                <span className="session-card__helper">
                  {statusConfig.helper}
                </span>
              </div>

              <h2 className="session-card__title">{session.theme}</h2>
              <p className="session-card__description">
                {session.description}
              </p>
            </div>
          </div>

          <div className="session-card__progress-block">
            <div className="session-card__progress-row">
              <span className="session-card__progress-text">
                <FormattedMessage
                  id={'gritx.sessions.sessionCompletion'}
                  defaultMessage="Session completion"
                />
              </span>
              <span className="session-card__progress-percent">
                {session.percentage}%
              </span>
            </div>

            <ProgressBar progress={session.percentage} progressStyleClass={progressBarClass}/>
          </div>

          <div className="session-card__actions">
            <button
              disabled={isStartDisabled}
              className={getButtonClassName('button--primary', isStartDisabled)}
              onClick={handleStartSession}
            >
              <FormattedMessage
                id={'gritx.sessions.start'}
                defaultMessage="Start"
              />
            </button>

            <button
              disabled={isContinueDisabled}
              className={getButtonClassName('button--primary', isContinueDisabled)}
              onClick={handleStartSession}
            >
              <FormattedMessage
                id={'gritx.sessions.continue'}
                defaultMessage="Continue"
              />
            </button>

            <button
              disabled={isStartOverDisabled}
              className={getButtonClassName(
                'button--secondary',
                isStartOverDisabled
              )}
              onClick={handleRemoveSession}
            >
              <FormattedMessage
                id={'gritx.sessions.startOver'}
                defaultMessage="Start Over"
              />
            </button>
          </div>
        </div>
      </div>

    </article>
  );
};
